/* Copyright (c)2025 Sky Co., LTD. All rights reserved. */

import { defineStore } from 'pinia';
import { MgrRestriction } from "@/models/MgrRestriction/MgrRestriction";
import { ScvLicenseManager } from '@/models/ScvLicenseManager';
import { RestrictionRequireLicense } from '@/models/MgrRestriction/RestrictionData'

export const useUserSettingStore = defineStore('userSetting', {
    state: () => ({
        // データの取得を行ったかどうか
        isFetched: false,

        // 制限機能ビット
        mgrRestriction: new MgrRestriction(),

        // 所属部署ID
        divisionId: -1,

        // 所属部署名
        divisionName: '',

        // 閲覧可能部署ID（※親IDのみで配下部署のIDは入らない）
        viewableDivisionIds: new Array<number>(),

        // システムアカウントかどうか
        isSystem: false,

        // BitLocker情報が存在するかどうか
        existsBitLocker: false,

        // ユーザー名
        userName: '',

        // ライセンス情報
        scvLicenseManager: new ScvLicenseManager()
    }),
    actions: {
        setUserSetting(bit: string, divisionId: number, divisionName: string, viewableDivisionIds: Array<number>, isSystem: boolean, existsBitLocker: boolean, userName: string, scvLicenseManager: ScvLicenseManager) {
            if (bit == null || bit.length <= 0) {
                bit = this.mgrRestriction.getMaxString(); // 設定されていない場合は全許可
            }
            this.mgrRestriction.FromString(bit);
            this.divisionId = divisionId;
            this.divisionName = divisionName;
            this.viewableDivisionIds = viewableDivisionIds;
            this.isSystem = isSystem;
            this.isFetched = true;
            this.existsBitLocker = existsBitLocker;
            this.userName = userName;
            this.scvLicenseManager = scvLicenseManager;
        }
    },
    getters: {
        isEnableFunction: (state) => {
            return (bit: number): boolean => {

                // 必要なライセンスシリアルビットを取得(ver21.0～)
                let hasLicenseSerial = false;
                const functionBits = RestrictionRequireLicense.getRestrictionRequireLicenseMap(bit);
                // 見つかった場合は、機能IDにマッピングされたライセンスシリアルを持っているかをチェック
                if (undefined !== functionBits) {
                    for (const functionBit of functionBits) {
                        if (state.scvLicenseManager.isLicenseEnable(functionBit)) {
                            // 複数のライセンスシリアルが定義されている場合は、１つでも持っていればOKとする
                            hasLicenseSerial = true;
                            break;
                        }
                    }
                }
                else {
                    // 機能IDに必要なライセンス定義がない場合(undefined)はライセンスは必要ない判断
                    hasLicenseSerial = true;
                }

                // ライセンスが1つもない場合は制限する。
                if (!hasLicenseSerial) {
                    return false;
                }

                if (state.isSystem) {
                    // システムアカウントなら機能制限なし
                    return true;
                }

                return state.mgrRestriction.Get(bit);
            }
        },
        isEnableFunctionAtLeastOne: (state) => {
            return (bitList: Array<number>): boolean => {

                // 必要なライセンスシリアルビットを取得(ver21.0～)
                let hasLicenseSerial = false;
                for (let i = 0; i < bitList.length; i++) {

                    // 見つかった場合は、機能IDにマッピングされたライセンスシリアルを持っているかをチェック
                    const functionBits = RestrictionRequireLicense.getRestrictionRequireLicenseMap(bitList[i]);
                    if (undefined !== functionBits) {
                        for (const functionBit of functionBits) {
                            if (state.scvLicenseManager.isLicenseEnable(functionBit)) {
                                // 複数のライセンスシリアルが定義されている場合は、１つでも持っていればOKとする
                                hasLicenseSerial = true;
                                break;
                            }
                        }
                    }
                    else {
                        // 機能IDに必要なライセンス定義がない場合(undefined)はライセンスは必要ない判断
                        hasLicenseSerial = true;
                    }

                    // すでにライセンスがあることが判明しているなら以降の判定は不要
                    if (hasLicenseSerial) {
                        break;
                    }
                }

                // ライセンスが1つもない場合は制限する。
                if (!hasLicenseSerial) {
                    return false;
                }

                if (state.isSystem) {
                    // システムアカウントなら機能制限なし
                    return true;
                }

                let res = false;
                for (let i = 0; i < bitList.length; i++) {
                    if (state.mgrRestriction.Get(bitList[i])) {
                        res = state.mgrRestriction.Get(bitList[i]);
                    }
                }
                return res;
            }
        },
        getExistsBitLocker: (state) => {
            return state.existsBitLocker;
        },
        getUserName: (state) => {
            return state.userName;
        },
        getDivisionId: (state) => {
            // 所属部署が指定されていない場合はネットワーク全体とする
            if (state.divisionId <= 0) {
                return 1;
            }
            return state.divisionId;
        },
        getDivisionName: (state) => {
            // 所属部署が指定されていない場合はネットワーク全体とする
            if (state.divisionId <= 0) {
                return 'ネットワーク全体'
            }
            return state.divisionName;
        },
        getViewableDivisionIds: (state) => {
            const divisionIds = [...state.viewableDivisionIds];
            if (0 < state.divisionId) {
                divisionIds.push(state.divisionId);
            }

            // 閲覧可能部署も所属部署も指定されていない場合はnullを返す
            if (divisionIds.length === 0) {
                return null;
            }

            return divisionIds;
        },
        getIsFetched: (state) => {
            return state.isFetched;
        },
        getScvLicenseManager: (state) => {
            return state.scvLicenseManager;
        },
        canViewAllDivision: (state) => {
            // システムアカウント、または1:ネットワーク全体が閲覧可能な場合は全部署閲覧可能
            if (state.isSystem || (state.viewableDivisionIds && state.viewableDivisionIds.includes(1))) {
                return true;
            }
            return false;
        },
    },
});

/* Copyright (c)2025 Sky Co., LTD. All rights reserved. */
